import { Fragment, type Ref } from 'react';
import styled from 'styled-components';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import {
  CellGrow,
  SearchTableRow,
} from 'components/searchPlayerTable/SearchTable/ui';
import { PROJECTION_WITH_DIFFERENCE_BY_SPORT } from 'lib/projection';
import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';

import { FirstCellSkeleton } from './FirstCellSkeleton';
import { LastCellSkeleton } from './LastCellSkeleton';
import { MatchupsSkeleton } from './MatchupsSkeleton';
import { ScoreSkeleton } from './ScoreSkeleton';
import { UpcomingGamesSkeleton } from './UpcomingGamesSkeleton';

type Props = {
  variant?: 'COMPOSE' | 'SCOUTING';
  ref?: Ref<HTMLDivElement>;
};

const StatsSkeleton = styled(Skeleton)`
  height: var(--double-and-a-half-unit);
  width: var(--quadruple-unit);
`;

export const SearchPlayerRowSkeleton = ({
  variant = 'SCOUTING',
  ref,
}: Props) => {
  const {
    flags: { useHandleL10Gain = false },
  } = useFeatureFlags();

  const { sport } = useSportContext();
  const withDifference =
    useHandleL10Gain && PROJECTION_WITH_DIFFERENCE_BY_SPORT[sport];
  const { statsManager } = useManagePlayerSearchView();
  return (
    <SearchTableRow ref={ref}>
      <FirstCellSkeleton variant={variant} withAction />
      {variant === 'COMPOSE' && <MatchupsSkeleton />}
      {variant === 'SCOUTING' && <UpcomingGamesSkeleton />}
      {statsManager[PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE].map(e => {
        return <ScoreSkeleton variant="average-score" key={e} />;
      })}
      {statsManager[PlayerSearchViewAttributes.PROJECTION].map(e => {
        if (withDifference) {
          return (
            <Fragment key={e}>
              <Vertical center>
                <StatsSkeleton />
              </Vertical>
              <Vertical center>
                <StatsSkeleton />
              </Vertical>
            </Fragment>
          );
        }
        return (
          <Vertical key={e} center>
            <StatsSkeleton />
          </Vertical>
        );
      })}
      {statsManager[PlayerSearchViewAttributes.PROJECTION_GRADE].map(e => {
        return (
          <Vertical key={e} center>
            <StatsSkeleton />
          </Vertical>
        );
      })}
      {[
        ...statsManager[PlayerSearchViewAttributes.BREAKDOWN],
        ...statsManager[PlayerSearchViewAttributes.STATS],
      ].map(e => {
        return (
          <Vertical key={e} center>
            <StatsSkeleton />
          </Vertical>
        );
      })}
      {variant === 'SCOUTING' && (
        <>
          <CellGrow />
          <LastCellSkeleton />
        </>
      )}
    </SearchTableRow>
  );
};
